<template>
  <section
    id="information"
    class="lpu-information d-flex flex-column"
    :class="isMobile ? 'ga-5' : 'ga-8'"
    data-qa="lpu_information"
  >
    <LpuPhotos
      v-if="lpu.lpu_photos?.length"
      :photos="lpu.lpu_photos"
      :lpu-name="lpu.name"
    />

    <div
      class="lpu-information__body"
      :class="isMobile ? 'd-flex flex-column ga-5' : 'd-flex align-start ga-8'"
    >
      <InformationCard
        class="lpu-information__card"
        :class="{
          'lpu-information__card--desktop': !isMobile,
          'order-1': isMobile,
        }"
        :lpu="lpu"
        :license-items="licenseItems"
      />

      <div class="lpu-information__content d-flex flex-column ga-5">
        <h2
          class="text--text"
          :class="isMobile ? 'text-h6' : 'text-h5'"
          data-qa="lpu_information_title"
        >
          {{ tStatic('Информация') }}
        </h2>

        <template v-if="hasAmenities">
          <InformationTextBlock
            v-if="descriptionHtml"
            :title="tStatic('Описание')"
            :html="descriptionHtml"
          />
          <InformationTextBlock
            v-if="servicesHtml"
            :title="tStatic('Услуги')"
            :html="servicesHtml"
          />
        </template>
        <p
          v-else
          class="text-body-2 text--text ma-0"
        >
          {{ tStatic('Нет описания') }}.
        </p>

        <LpuMedals :lpu="lpu" />

        <div
          v-if="hasAmenities && (transportHtml || parkingHtml)"
          class="lpu-information__geo d-flex"
          :class="isMobile ? 'flex-column ga-6' : 'ga-6'"
        >
          <InformationTextBlock
            v-if="transportHtml"
            :title="tStatic('Проезд')"
            :html="transportHtml"
          />
          <InformationTextBlock
            v-if="parkingHtml"
            :title="tStatic('Парковка')"
            :html="parkingHtml"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getInformationHtml from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/functions/getInformationHtml'
import getLicenseImages from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/functions/getLicenseImages'
import InformationCard from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/components/InformationCard.vue'
import InformationTextBlock from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuInformation/components/InformationTextBlock.vue'
import LpuMedals from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuMedals/LpuMedals.vue'
import LpuPhotos from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuPhotos/LpuPhotos.vue'
import type {
  Lpu,
  LpuInformation,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'

const props = defineProps<{
  lpu: Lpu
  lpuInformation?: LpuInformation | null
}>()

const isMobile = useIsMobile()
const { context }: CommonContextStore = useContextStore()
const allowedHosts = context.ALLOWED_HOSTS ?? []

const licenseTitle = computed(() => tStatic('Лицензия'))
const licenseItems = computed(() => getLicenseImages(props.lpu.license_images, licenseTitle.value))

const hasAmenities = computed(() => Boolean(props.lpuInformation?.has_amenities_for_main_page))

const descriptionHtml = computed(() => getInformationHtml(
  tDynamic(props.lpuInformation, 'description'),
  { stripContacts: true, allowedHosts },
))
const servicesHtml = computed(() => getInformationHtml(
  tDynamic(props.lpuInformation, 'services'),
  { stripContacts: true, allowedHosts },
))
const transportHtml = computed(() => getInformationHtml(
  tDynamic(props.lpuInformation, 'transport_info'),
))
const parkingHtml = computed(() => getInformationHtml(
  tDynamic(props.lpuInformation, 'parking_info')
  || tDynamic(props.lpu, 'parking'),
))
</script>

<style scoped lang="scss">
.lpu-information {
  &__body {
    min-width: 0;
    width: 100%;
  }

  &__card {
    width: 100%;
    flex-shrink: 0;

    &--desktop {
      width: 420px;
    }
  }

  &__content {
    min-width: 0;
    flex: 1 1 auto;
  }

  &__geo {
    width: 100%;

    > * {
      flex: 1 1 0;
      min-width: 0;
    }
  }
}
</style>
